// Pure extraction logic — no network, no Supabase. The Edge Function is a thin adapter
// over this; the unit tests exercise it directly (constitution Principle 8, review B1/B4).

export type ItemType = "task" | "event" | "note" | "grocery";
export type Route = "cloud" | "private_health";

export interface Draft {
  type: ItemType;
  title: string;
  owners: string[]; // member display_names it's for (empty → defaults to creator at commit). REQ-CAPTURE-014
  due_at: string | null; // ISO 8601 — start (first occurrence for a recurring item)
  end_at: string | null; // ISO 8601 — end, when a range/duration was given (REQ-CAL-012)
  location: string | null; // a venue/place, kept out of the title (REQ-CAL-011)
  rrule: string | null; // RFC5545 recurrence rule when the item repeats (REQ-CAL-011)
  details: string | null; // free-format notes — a couple of sentences or a short list (REQ-ITEM-007)
  priority: string | null;
}

export interface Extraction {
  draft: Draft;
  blockingField: string | null; // "due_at" | "title" | "type" | null
  clarifyingQuestion: string | null;
  ready: boolean; // true ⇔ no blocking field. The DB commit gate requires ready === true.
}

/** The boundary predicate. The Edge Function short-circuits on this BEFORE any Claude client
 *  is constructed — clinical content on a health route never reaches a model (Principle 2). */
export function isCloudRoute(route: string): boolean {
  return route === "cloud";
}
export function shouldExtract(route: string): boolean {
  return isCloudRoute(route);
}

// Blocking-field precedence (exactly one question at a time): due_at → title → type.
const QUESTION: Record<string, string> = {
  due_at: "When is it?",
  title: "What should I call it?",
  type: "Is this a task, event, note, or grocery?",
};

const VALID_TYPES: ItemType[] = ["task", "event", "note", "grocery"];

/** Structured output the model is constrained to return (before our rules run). */
export interface ModelExtraction {
  type: string;
  title: string;
  owners?: string[] | null; // the family members it's for (a list; may be [child] pending "who_takes")
  due_at?: string | null;
  end_at?: string | null;
  location?: string | null;
  rrule?: string | null;
  details?: string | null;
  priority?: string | null;
  needs_clarification?: string[]; // fields the model judged missing/ambiguous (incl. "who_takes")
}

function computeBlocking(draft: Draft, modelNeeds: string[]): string | null {
  // Structural requirements + the model's own ambiguity flags, resolved by precedence.
  const missing = new Set(modelNeeds);
  if (!draft.title || !draft.title.trim()) missing.add("title");
  if (!VALID_TYPES.includes(draft.type)) missing.add("type");
  if (draft.type === "event" && !draft.due_at) missing.add("due_at");
  for (const field of ["due_at", "title", "type"]) {
    if (missing.has(field)) return field;
  }
  return null;
}

/** The UTC instant for a wall-clock time in `tz` — DST-correct via Intl (no library). Given the naive
 *  parts, guess the instant as if UTC, read back what clock time that shows in `tz`, and correct by
 *  the resulting offset. */
function wallTimeToUtcISO(y: number, mo: number, d: number, h: number, mi: number, s: number, tz: string): string {
  const guess = Date.UTC(y, mo - 1, d, h, mi, s);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date(guess));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  let hh = get("hour");
  if (hh === 24) hh = 0; // some engines render midnight as 24
  const shown = Date.UTC(get("year"), get("month") - 1, get("day"), hh, get("minute"), get("second"));
  return new Date(guess - (shown - guess)).toISOString();
}

/** Normalise a model datetime to a proper UTC instant (REQ-CAL-015). The model is INCONSISTENT about
 *  timezones: it sometimes returns a full offset (`…+12:00` / `…Z`, already an instant — trust it) and
 *  sometimes a NAIVE wall-clock (`2026-09-11T18:00:00`, no offset) which Postgres would store as UTC —
 *  off by NZ's +12/+13. A naive datetime is interpreted as `tz` local and converted. A date-only value
 *  (all-day, `2026-09-11`) and null pass through untouched. */
export function normalizeInstant(value: string | null | undefined, tz = "Pacific/Auckland"): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const v = value.trim();
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(v)) return v; // already carries an offset → an instant
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return v; // date-only or unrecognised → leave as-is (downstream treats as all-day / passthrough)
  return wallTimeToUtcISO(+m[1], +m[2], +m[3], +m[4], +m[5], m[6] ? +m[6] : 0, tz);
}

/** Normalise the model's structured output into a Draft + blocking decision. Deterministic. */
export function parseExtraction(model: ModelExtraction, tz = "Pacific/Auckland"): Extraction {
  const draft: Draft = {
    type: (VALID_TYPES as string[]).includes(model.type) ? (model.type as ItemType) : ("" as ItemType),
    title: (model.title ?? "").trim(),
    owners: Array.isArray(model.owners) ? model.owners.filter((o): o is string => typeof o === "string" && o.trim().length > 0) : [],
    due_at: normalizeInstant(model.due_at, tz),
    end_at: normalizeInstant(model.end_at, tz),
    location: (model.location ?? null) || null,
    rrule: (model.rrule ?? null) || null,
    details: (model.details ?? null) || null,
    priority: model.priority ?? null,
  };
  const blockingField = computeBlocking(draft, model.needs_clarification ?? []);
  return {
    draft,
    blockingField,
    clarifyingQuestion: blockingField ? QUESTION[blockingField] : null,
    ready: blockingField === null,
  };
}

/** Apply a member's answer to the blocking field, then recompute blocking/ready.
 *  Used by the client clarify loop before it patches capture.extraction. */
export function resolveField(extraction: Extraction, field: string, value: string, tz = "Pacific/Auckland"): Extraction {
  const draft: Draft = { ...extraction.draft };
  if (field === "due_at") draft.due_at = normalizeInstant(value, tz);
  else if (field === "title") draft.title = value.trim();
  else if (field === "type" && (VALID_TYPES as string[]).includes(value)) draft.type = value as ItemType;
  const blockingField = computeBlocking(draft, []);
  return {
    draft,
    blockingField,
    clarifyingQuestion: blockingField ? QUESTION[blockingField] : null,
    ready: blockingField === null,
  };
}

// ── Multi-item capture (REQ-CAPTURE-010) — one input → several proposed drafts → a review list.
// The MODEL proposes multiple structured extractions (a newsletter, the term-dates image); this is
// the pure POST-PROCESSING over them: classify context-vs-actionable, dedupe against what we already
// have, and default the select state. Extends the single-item seam — it does not fork it.
export type DraftKind = "actionable" | "context";
export interface MultiDraft {
  extraction: Extraction;
  kind: DraftKind; // a term/holiday band is context (a span), not four to-dos
  duplicate: boolean; // already present locally or on a subscribed calendar (006)
  selected: boolean; // default: keep the fresh actionable ones; unselect dupes (user stays in control)
  needsAdult: boolean; // a child's event awaiting an adult to be appointed to take them (REQ-CAPTURE-014)
}
export interface MultiDraftModel {
  drafts: MultiDraft[];
}

/** A term/holiday band is context, a point is actionable (capture-flow-spec §5). Pure over the title. */
export function classifyDraft(draft: Draft): DraftKind {
  return /\bterm\s*\d|\bterm\b|holiday|teacher[- ]only|\bmid[- ]?term\b|school break/i.test(draft.title)
    ? "context"
    : "actionable";
}

/** A minimal reference to something we already have (a local item or a subscribed-calendar event). */
export interface ExistingRef {
  title: string;
  due_at: string | null;
  type: string;
}
const normTitle = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : null);

/** Dedupe predicate keyed on (normalised title, day, type). Near-duplicates are surfaced in the
 *  review list unselected — never silently dropped (critic-007 R4). */
export function isDuplicate(candidate: Draft, existing: ExistingRef[]): boolean {
  return existing.some(
    (e) => normTitle(e.title) === normTitle(candidate.title) && e.type === candidate.type && day(e.due_at) === day(candidate.due_at)
  );
}

/** Turn the model's proposed extractions into a reviewable set: classify, dedupe, and set the
 *  default selection. One commit of the kept set (the caller). Pure. */
export function multiDraftModel(models: ModelExtraction[], existing: ExistingRef[] = []): MultiDraftModel {
  const drafts = models.map((m): MultiDraft => {
    const extraction = parseExtraction(m);
    const kind = classifyDraft(extraction.draft);
    const duplicate = isDuplicate(extraction.draft, existing);
    const needsAdult = (m.needs_clarification ?? []).includes("who_takes");
    return { extraction, kind, duplicate, selected: !duplicate, needsAdult };
  });
  return { drafts };
}

/** The inbound-review variant (REQ-INGEST-004): build the reviewable set from already-parsed
 *  Extractions (ingested captures store the parsed extraction, not raw model output). Dedupes each
 *  against what the family already has and defaults-select only the fresh, ready, non-duplicate ones. */
export function multiDraftFromExtractions(extractions: Extraction[], existing: ExistingRef[] = []): MultiDraftModel {
  const drafts = extractions.map((extraction): MultiDraft => {
    const kind = classifyDraft(extraction.draft);
    const duplicate = isDuplicate(extraction.draft, existing);
    return { extraction, kind, duplicate, selected: !duplicate && extraction.ready, needsAdult: false };
  });
  return { drafts };
}

// The per-item structured shape — one extraction. Shared by the single- and multi-item builders.
const ITEM_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    type: { type: "string", enum: VALID_TYPES },
    title: { type: "string" },
    owners: { type: "array", items: { type: "string" } },
    due_at: { type: ["string", "null"] },
    end_at: { type: ["string", "null"] },
    location: { type: ["string", "null"] },
    rrule: { type: ["string", "null"] },
    details: { type: ["string", "null"] },
    priority: { type: ["string", "null"] },
    needs_clarification: { type: "array", items: { type: "string" } },
  },
  required: ["type", "title", "owners", "due_at", "end_at", "location", "rrule", "details", "priority", "needs_clarification"],
} as const;

/** A human LOCAL now for the prompt (REQ-CAL-016). The caller passes a UTC instant; the model must
 *  resolve "today"/"tonight"/"next Tue" against the FAMILY's local date, not the UTC date (which is a
 *  day behind NZ every evening). e.g. "Monday 17 August 2026, 07:50". */
export function localNowDescription(nowISO: string, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date(nowISO));
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${g("weekday")} ${g("day")} ${g("month")} ${g("year")}, ${g("hour")}:${g("minute")}`;
}

/** An explicit weekday→date table for the next `days` local days (REQ-CAL-017). LLMs miscount dates
 *  ("Friday" → the wrong day), so we hand them a lookup instead of making them do the arithmetic. */
export function localDateTable(nowISO: string, tz: string, days = 14): string {
  const base = Date.parse(nowISO);
  const fmt = (ms: number) => {
    const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz, weekday: "long", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(ms));
    const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
    return { wd: g("weekday"), iso: `${g("year")}-${g("month")}-${g("day")}` };
  };
  const rows: string[] = [];
  for (let i = 0; i < days; i++) {
    // +12h then +i days keeps us at local midday so a DST shift never skips/duplicates a date.
    const { wd, iso } = fmt(base + 43_200_000 + i * 86_400_000);
    rows.push(`${wd} ${iso}${i === 0 ? " (today)" : i === 1 ? " (tomorrow)" : ""}`);
  }
  return rows.join(", ");
}

function extractionGuidance(memberNames: string[], nowISO?: string, tz = "Pacific/Auckland"): string {
  const when = nowISO
    ? `The current LOCAL date and time (timezone ${tz}) is ${localNowDescription(nowISO, tz)}. ` +
      `Resolve any weekday or relative day by LOOKING IT UP in this date table — do NOT compute dates yourself: ${localDateTable(nowISO, tz)}. ` +
      `"today"/"tonight"=today's row, "tomorrow"=the next row, a bare weekday ("Friday")=its FIRST occurrence at/after today in the table, "next Friday"=the following week's. ` +
      `Return due_at and end_at as the LOCAL wall-clock time with NO timezone offset — "2026-08-17T15:00" for 3pm. ` +
      `Infer a sensible time from the KIND of event even when none is stated: breakfast ~08:00, morning tea ~10:00, lunch ~12:00, afternoon tea ~15:00, dinner ~18:30, drinks ~17:30 — e.g. "lunch with Mark on Friday" → 2026-08-21T12:00. Only when there is genuinely no time and none can be inferred, return a DATE-ONLY value ("2026-08-17"), never a midnight time. The app converts local time to the correct instant. `
    : "";
  return (
    "Pick the best type (task, event, note, grocery). " +
    "Make the title SHORT and scannable (e.g. \"Appt: Dr Chong\", not \"Appointment with Richard Chong " +
    "at Highbrook Medical\"). Put any venue/place in `location` (e.g. \"Highbrook Medical\"), NEVER in the title. " +
    "Set `owners` to the family members it is for (a list of names; empty if none are named). " +
    "If a time RANGE or duration is given (\"12:15–1pm\", \"3-4pm\", \"for 90 min\"), set `end_at` to the end as ISO 8601. " +
    "If the item REPEATS (\"every Thursday evening\", \"weekly\", \"each morning\", \"monthly\"), set `rrule` to an " +
    "RFC5545 rule (e.g. FREQ=WEEKLY;BYDAY=TH) and set due_at to the FIRST occurrence's date/time. Else rrule is null. " +
    "If the note carries EXTRA specifics worth keeping — a short instruction, a checklist, what to bring — " +
    "put them verbatim-ish in `details` (a couple of sentences or a short list; keep the title itself short). " +
    "Never invent details that were not stated; leave `details` null when there are none. " +
    "Fold any amount or key figure into the title (e.g. \"$45 photo fee\"). " +
    `Known family members: ${memberNames.join(", ") || "(unknown)"}. ` +
    when +
    "List in needs_clarification any field that is missing or genuinely ambiguous. " +
    "Never invent a date you cannot infer from the note."
  );
}

/** Optional family roles for the multi-item prompt (REQ-CAPTURE-014). */
export interface FamilyRoles {
  children: string[];
  parents: string[];
}
function childRuleGuidance(family?: FamilyRoles): string {
  if (!family) return "";
  return (
    `Family children: ${family.children.join(", ") || "(none)"}. Adults/parents: ${family.parents.join(", ") || "(none)"}. ` +
    "For an appointment or activity that is for or about a CHILD, set `owners` to that child, and add " +
    "\"who_takes\" to needs_clarification so an adult can be appointed to take them — do NOT guess which adult. "
  );
}

/** Build the pieces of the Claude Messages API request. The Edge Function assembles the
 *  actual HTTP call; kept here so the prompt + schema are covered by a unit test. */
export function buildExtractionRequest(
  text: string,
  memberNames: string[],
  nowISO?: string,
  tz = "Pacific/Auckland"
) {
  const system =
    "You turn a family's short note into a single structured item for a household app. " +
    extractionGuidance(memberNames, nowISO, tz);
  return { system, schema: ITEM_SCHEMA, userText: text };
}

// ── Conversational refine (REQ-CAL-014) — edit an existing item by a natural-language instruction.
/** The current item state handed to the refine prompt (only the fields the editor can change). */
export interface RefineInput {
  type?: string | null;
  title?: string | null;
  owners?: string[] | null;
  due_at?: string | null;
  end_at?: string | null;
  location?: string | null;
  rrule?: string | null;
  details?: string | null;
}
/** Build the refine request: the model gets the item's CURRENT state + the member's instruction and
 *  returns the COMPLETE updated item (same ITEM_SCHEMA), preserving every field the change doesn't
 *  touch. The app parses the result with parseExtraction and prefills the Edit form for review —
 *  nothing is saved without the member confirming (Principle 7). */
export function buildRefineRequest(
  current: RefineInput,
  instruction: string,
  memberNames: string[],
  nowISO?: string,
  tz = "Pacific/Auckland"
) {
  const currentJson = JSON.stringify({
    type: current.type ?? null,
    title: current.title ?? "",
    owners: current.owners ?? [],
    due_at: current.due_at ?? null,
    end_at: current.end_at ?? null,
    location: current.location ?? null,
    rrule: current.rrule ?? null,
    details: current.details ?? null,
  });
  const system =
    "You are editing ONE existing household item. Its current state is:\n" +
    currentJson +
    "\nApply the member's requested change and return the COMPLETE updated item. Keep every field the " +
    "change does not affect exactly as it is now (do not blank or invent fields). " +
    extractionGuidance(memberNames, nowISO, tz);
  return { system, schema: ITEM_SCHEMA, userText: instruction };
}

/** Multi-item variant (REQ-CAPTURE-010): one input may describe SEVERAL items — a school
 *  newsletter, a term-dates photo. The model returns an `items` array (usually length 1); the
 *  app runs multiDraftModel over them and shows a review list only when more than one comes back. */
export function buildMultiExtractionRequest(
  text: string,
  memberNames: string[],
  nowISO?: string,
  tz = "Pacific/Auckland",
  family?: FamilyRoles,
  preferActionable = false
) {
  // Ingested email (REQ-INGEST-003/008): three tiers. DO → a `task`/`event` when the family MUST act
  // with an external party. KNOW → a `note` (an FYI "heads-up") when there is no action needed but the
  // family would want to SEE it (a charge/bill/payment notice, a delivery worth noting, an account or
  // service change, a policy/price update). IGNORE → an EMPTY list for pure noise (promos, marketing,
  // password/username/security resets, parcel tracking, receipts, gift codes, social/newsletter FYI).
  // One entry per email/thread. (Typed capture is unaffected — it keeps notes and always returns ≥1.)
  const actionable = preferActionable
    ? "This input is an inbound email. Sort it into ONE of three outcomes:\n" +
      "• DO — return a `task` or `event` when the family MUST take an action involving an EXTERNAL party " +
      "or a real obligation: pay an invoice or a bill that is DUE, renew or pay a vehicle registration / " +
      "licence / rego / WOF, pay council rates or insurance, settle an OVERDUE or LAPSED account, " +
      "collect/pick up an order, book or attend an appointment, RSVP, submit a form or document, or send " +
      "a genuinely required reply. Anything overdue, lapsed, or with a payment/renewal deadline is a DO. " +
      "Verb-first title, key reference numbers + the due date/amount folded in.\n" +
      "• KNOW — return a `note` (a short 'heads-up') when no action is strictly required but the family " +
      "would want to SEE it. This INCLUDES **real correspondence**: a genuine reply or message from an " +
      "actual person, or from an organisation the family deals with — a school or teacher (about a child, " +
      "homework, an event, a report), a tradesperson, a supplier or customer in an ongoing thread, a " +
      "service or utility provider, a council or government body. It ALSO includes: a charge / bill / " +
      "payment or excess-usage notice, a subscription or service change, an account or security-relevant " +
      "change worth knowing, a delivery or order update that matters, a price or policy change. Give a " +
      "one-line factual summary as the title (who it's from + what it's about). Do NOT invent an action.\n" +
      "• IGNORE — return an EMPTY `items` list ONLY for automated, bulk, or impersonal noise: promotions " +
      "and marketing, newsletters and digests, username/password/security-code resets, parcel/shipping " +
      "tracking, plain order confirmations and receipts, gift-code/reward/voucher offers, app-update or " +
      "build notifications, and social 'FYI' updates.\n" +
      "Return at most ONE item per email/thread. TIE-BREAKERS: a message from a REAL PERSON, or from a " +
      "school / service / business contact the family actually deals with, defaults to KNOW — do NOT " +
      "IGNORE it just because it names no explicit action; only clearly automated, bulk, or marketing " +
      "mail is IGNORE. Between DO and KNOW, prefer KNOW unless an action is genuinely required. "
    : "";
  const system =
    "You turn a family's note or photo into one or more structured items for a household app. " +
    "Most inputs are a single item — return exactly one. A newsletter, roster, or term-dates image " +
    "may list several distinct events, tasks, or context spans (a term/holiday band): return one " +
    "entry per distinct item, and do not split a single item across entries. " +
    actionable +
    childRuleGuidance(family) +
    extractionGuidance(memberNames, nowISO, tz);
  const schema = {
    type: "object",
    additionalProperties: false,
    // Ingested mail may yield NOTHING actionable → allow an empty list; typed capture needs ≥1.
    properties: { items: { type: "array", minItems: preferActionable ? 0 : 1, items: ITEM_SCHEMA } },
    required: ["items"],
  };
  return { system, schema, userText: text };
}
